// PropertyListing.jsx — Server-side paginated property listing
// - All filtering/sorting delegated to backend
// - Only PAGE_SIZE records in memory at any time
// - Skeleton loading state while fetching
// - Debounced search input

import React, { useState, useCallback, useContext, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import './PropertyListing.css';

import { MyContext } from '../../../../../Context/MyContext';
import FilterPanel from '../../filter/FilterPanel';
import PropertyCard from './PropertyCard';
import { saveRecentSearch, buildSearchLabel, buildSearchUrl } from '../../../../../utils/userActivity';
import AppIcon from '../../../../../Components/AppIcon';

const DEFAULT_PAGE_SIZE = 20;

// ── URL ↔ state ───────────────────────────────────────────────
const tabToListingType = (tab) => {
  if (tab === 'rent') return 'rent';
  if (tab === 'buy') return 'sale';
  return 'all';
};

const buildFilters = (p) => {
  const tab = p.get('tab');
  const typeParam = p.get('type');
  const listing_type = typeParam && typeParam !== 'all'
    ? typeParam
    : tabToListingType(tab);
  return {
  listing_kind:  p.get('kind')   || 'all',
  listing_type,
  property_type: p.get('ptype')  || 'all',
  sort:          p.get('sort')   || null,
  search:        p.get('q')      || '',
  state_id:      p.get('state')  || null,
  city_id:       p.get('city')   || null,
  category_id:   p.get('cat')    ? p.get('cat').split(',')  : [],
  bhk:           p.get('bhk')    ? p.get('bhk').split(',')  : [],
  sales_type:    p.get('sale')   ? p.get('sale').split(',') : [],
  direction:     p.get('dir')    ? p.get('dir').split(',')  : [],
  amenities_id:  p.get('am')     ? p.get('am').split(',')   : [],
  pmin:          p.get('pmin')   ? Number(p.get('pmin'))    : null,
  pmax:          p.get('pmax')   ? Number(p.get('pmax'))    : null,
  limit:         p.get('limit')  ? Number(p.get('limit'))   : DEFAULT_PAGE_SIZE,
  };
};

const toParams = (f) => {
  const p = new URLSearchParams();
  if (f.listing_kind  !== 'all') p.set('kind',  f.listing_kind);
  if (f.listing_type  !== 'all') p.set('type',  f.listing_type);
  if (f.property_type !== 'all') p.set('ptype', f.property_type);
  if (f.sort)                    p.set('sort',  f.sort);
  if (f.search)                  p.set('q',     f.search);
  if (f.state_id)                p.set('state', f.state_id);
  if (f.city_id)                 p.set('city',  f.city_id);
  if (f.category_id?.length)     p.set('cat',   f.category_id.join(','));
  if (f.bhk?.length)             p.set('bhk',   f.bhk.join(','));
  if (f.sales_type?.length)      p.set('sale',  f.sales_type.join(','));
  if (f.direction?.length)       p.set('dir',   f.direction.join(','));
  if (f.amenities_id?.length)    p.set('am',    f.amenities_id.join(','));
  if (f.pmin != null)            p.set('pmin',  f.pmin);
  if (f.pmax != null)            p.set('pmax',  f.pmax);
  if (f.limit)                   p.set('limit', f.limit);
  return p;
};

const fmt = (n) => {
  if (!n) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

// ── Skeleton card ─────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="pl-skeleton-card">
    <div className="pl-skel-img" />
    <div className="pl-skel-body">
      <div className="pl-skel-line pl-skel-title" />
      <div className="pl-skel-line pl-skel-sub" />
      <div className="pl-skel-line pl-skel-price" />
    </div>
  </div>
);

// ── Active chips ──────────────────────────────────────────────
const ActiveChips = ({ filters, onChange }) => {
  const { categorys, cities, aminities } = useContext(MyContext);
  const chips = [];
  if (filters.listing_kind !== 'all')
    chips.push({ label: filters.listing_kind, clear: () => onChange({ listing_kind: 'all' }) });
  if (filters.listing_type !== 'all')
    chips.push({ label: filters.listing_type, clear: () => onChange({ listing_type: 'all' }) });
  if (filters.property_type !== 'all')
    chips.push({ label: filters.property_type, clear: () => onChange({ property_type: 'all', category_id: [] }) });
  if (filters.search)
    chips.push({ label: `"${filters.search}"`, clear: () => onChange({ search: '' }) });
  if (filters.city_id) {
    const c = cities.find(x => String(x.id) === String(filters.city_id));
    chips.push({ label: c?.city_name || filters.city_id, clear: () => onChange({ city_id: null }) });
  }
  (filters.category_id || []).forEach(id => {
    const c = categorys.find(x => String(x.id) === id);
    chips.push({ label: c?.category_name || id, clear: () => onChange({ category_id: filters.category_id.filter(v => v !== id) }) });
  });
  (filters.bhk || []).forEach(b =>
    chips.push({ label: b, clear: () => onChange({ bhk: filters.bhk.filter(v => v !== b) }) }));
  (filters.sales_type || []).forEach(s =>
    chips.push({ label: s, clear: () => onChange({ sales_type: filters.sales_type.filter(v => v !== s) }) }));
  (filters.amenities_id || []).forEach(id => {
    const a = aminities.find(x => String(x.id) === id);
    chips.push({ label: a?.aminitie_name || id, clear: () => onChange({ amenities_id: filters.amenities_id.filter(v => v !== id) }) });
  });
  if (filters.pmin != null || filters.pmax != null)
    chips.push({ label: `${fmt(filters.pmin)} – ${fmt(filters.pmax)}`, clear: () => onChange({ pmin: null, pmax: null }) });
  if (filters.sort)
    chips.push({ label: filters.sort.replace('_', ' '), clear: () => onChange({ sort: null }) });

  if (!chips.length) return null;
  return (
    <div className="pl-chips">
      {chips.map((c, i) => (
        <span key={i} className="pl-chip">
          {c.label}
          <button className="pl-chip-x" onClick={c.clear} aria-label="Remove filter"><AppIcon name="close" size={12} /></button>
        </span>
      ))}
    </div>
  );
};

// ── Pagination ────────────────────────────────────────────────
// ── Pagination ────────────────────────────────────────────────
const Pagination = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;

  const getPages = () => {
    const delta = 1;
    const rangeStart = Math.max(2, page - delta);
    const rangeEnd   = Math.min(totalPages - 1, page + delta);
    const pages = [];

    // Always first page
    pages.push(1);

    // Left ellipsis
    if (rangeStart > 2) pages.push('...');

    // Middle window
    for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);

    // Right ellipsis
    if (rangeEnd < totalPages - 1) pages.push('...');

    // Always last page
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const pages = getPages();

  return (
    <div className="pg-wrap">
      <button className="pg-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Prev
      </button>
      <div className="pg-pages">
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="pg-ellipsis">…</span>
          ) : (
            <button key={p} className={`pg-num ${p === page ? 'active' : ''}`} onClick={() => onChange(p)}>
              {p}
            </button>
          )
        )}
      </div>
      <button className="pg-btn" disabled={page === totalPages} onClick={() => onChange(page + 1)}>
        Next
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────
const PropertyListing = () => {
  const { fetchPropertiesPaged, categorys, cities, user, propertyListPageConfig } = useContext(MyContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const cardsOnlyMode = searchParams.get('view') === 'cards';
  const [mobileFilter, setMobileFilter] = useState(false);
  const [focusFiltersOpen, setFocusFiltersOpen] = useState(false);
  const [isCompactViewport, setIsCompactViewport] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth <= 900 : false)
  );
  const [page,         setPage]         = useState(1);
  const [filters,      setFilters]      = useState(() => buildFilters(searchParams));

  // Server-side result state
  const [results,     setResults]     = useState([]);
  const [total,       setTotal]       = useState(0);
  const [totalPages,  setTotalPages]  = useState(1);
  const [isLoading,   setIsLoading]   = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const topRef      = useRef(null);
  const cardsColRef = useRef(null);
  const loadMoreRef = useRef(null);
  const searchTimer = useRef(null);
  const hasLoadedRef = useRef(false);
  const fetchTokenRef = useRef(0);

  const availablePageSizes = useMemo(() => {
    const options = Array.isArray(propertyListPageConfig?.options)
      ? propertyListPageConfig.options.map(Number).filter(Number.isFinite)
      : [];
    return options.length ? options : [20, 50, 100];
  }, [propertyListPageConfig?.options]);

  const defaultPageSize = useMemo(() => {
    const configured = Number(propertyListPageConfig?.default);
    return availablePageSizes.includes(configured)
      ? configured
      : (availablePageSizes[0] || DEFAULT_PAGE_SIZE);
  }, [availablePageSizes, propertyListPageConfig?.default]);
  const pageSizeOptionsKey = availablePageSizes.join(',');

  const updateViewMode = useCallback((enabled) => {
    const next = new URLSearchParams(searchParams);
    if (enabled) next.set('view', 'cards');
    else next.delete('view');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const toParamsWithView = useCallback((nextFilters) => {
    const params = toParams(nextFilters);
    if (cardsOnlyMode) params.set('view', 'cards');
    return params;
  }, [cardsOnlyMode]);

  // ── Build API params from UI filters ──────────────────────
  const buildApiParams = useCallback((f, pg) => {
    const safeLimit = availablePageSizes.includes(Number(f.limit)) ? Number(f.limit) : defaultPageSize;
    const params = { page: pg, limit: safeLimit, status: 2 };

    if (f.listing_kind === 'property') params.is_project = false;
    if (f.listing_kind === 'project')  params.is_project = true;
    if (f.listing_type !== 'all')      params.listing_type = f.listing_type;
    if (f.sales_type?.length === 1)    params.sales_type = f.sales_type[0];
    if (f.category_id?.length === 1)   params.category_id = f.category_id[0];
    if (f.city_id)                     params.city_id = f.city_id;
    if (f.state_id)                    params.state_id = f.state_id;
    if (f.pmin != null)                params.pmin = f.pmin;
    if (f.pmax != null)                params.pmax = f.pmax;
    if (f.search)                      params.q = f.search;
    if (f.sort)                        params.sort = f.sort;
    if (f.amenities_id?.length)        params.amenities_id = f.amenities_id.map(String).join(',');
    if (f.bhk?.length)                 params.bhk = f.bhk.join(',');
    return params;
  }, [availablePageSizes, defaultPageSize]);

  // ── Fetch from server ──────────────────────────────────────
  const doFetch = useCallback(async (f, pg, options = {}) => {
    const { append = false } = options;
    const requestId = ++fetchTokenRef.current;
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);
    try {
      const result = await fetchPropertiesPaged(buildApiParams(f, pg));
      if (requestId !== fetchTokenRef.current) return;
      setResults((prev) => {
        if (!append) return result.data || [];
        const merged = [...prev, ...(result.data || [])];
        const seen = new Set();
        return merged.filter((item) => {
          const key = String(item?.id ?? item?.property_id ?? Math.random());
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });
      setTotal(result.total || 0);
      setTotalPages(result.pages || 1);
      hasLoadedRef.current = true;
    } catch (e) {
      console.log('PropertyListing fetch error:', e.message);
    } finally {
      if (requestId === fetchTokenRef.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [fetchPropertiesPaged, buildApiParams]);

  // ── Sync filters from URL params ──────────────────────────
  useEffect(() => {
    const newFilters = buildFilters(searchParams);
    const nextLimit = availablePageSizes.includes(Number(newFilters.limit))
      ? Number(newFilters.limit)
      : defaultPageSize;
    if (nextLimit !== Number(newFilters.limit)) {
      newFilters.limit = nextLimit;
      setSearchParams(toParamsWithView(newFilters), { replace: true });
      return;
    }
    setFilters(newFilters);
    setPage(1);
    doFetch(newFilters, 1, { append: false });
    if (cardsOnlyMode) {
      cardsColRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    }

    const hasActivity =
      newFilters.search
      || newFilters.listing_type !== 'all'
      || newFilters.listing_kind !== 'all'
      || newFilters.city_id
      || newFilters.state_id
      || (newFilters.category_id?.length > 0);

    if (hasActivity) {
      const city = cities.find((c) => String(c.id) === String(newFilters.city_id));
      const label = buildSearchLabel({
        ...newFilters,
        city_name: city?.city_name,
      });
      const url = buildSearchUrl(newFilters);
      saveRecentSearch({ label, url }, user).then(() => {
        window.dispatchEvent(new Event('recent-search-updated'));
      });
    }
  }, [searchParams, pageSizeOptionsKey, defaultPageSize]); // eslint-disable-line

  useEffect(() => {
    setFilters((prev) => {
      const nextLimit = availablePageSizes.includes(Number(prev.limit))
        ? Number(prev.limit)
        : defaultPageSize;
      if (nextLimit === Number(prev.limit)) return prev;
      const next = { ...prev, limit: nextLimit };
      setSearchParams(toParamsWithView(next), { replace: true });
      return next;
    });
  }, [pageSizeOptionsKey, defaultPageSize, setSearchParams, toParamsWithView]);

  useEffect(() => {
    setFocusFiltersOpen(false);
    setMobileFilter(false);
    if (cardsOnlyMode) {
      topRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
      cardsColRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [cardsOnlyMode]);

  useEffect(() => {
    const onResize = () => setIsCompactViewport(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!cardsOnlyMode) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setMobileFilter(false);
      setFocusFiltersOpen(false);
      updateViewMode(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [cardsOnlyMode, updateViewMode]);

  useEffect(() => {
    if (!cardsOnlyMode || !loadMoreRef.current || !cardsColRef.current) return undefined;
    const node = loadMoreRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setPage((prev) => {
          if (isLoadingMore || isLoading || prev >= totalPages) return prev;
          const nextPage = prev + 1;
          doFetch(filters, nextPage, { append: true });
          return nextPage;
        });
      },
      { root: cardsColRef.current, rootMargin: '280px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [cardsOnlyMode, doFetch, filters, isLoading, isLoadingMore, totalPages]);

  const onChange = useCallback((patch) => {
    // Keep the search input responsive; only debounce the URL/API sync
    if ('search' in patch) {
      const { _searchTyping, _searchCommit, ...rest } = patch;
      setFilters((prev) => {
        const next = { ...prev, ...rest };
        if (_searchCommit) {
          clearTimeout(searchTimer.current);
          setSearchParams(toParamsWithView(next), { replace: true });
        } else {
          clearTimeout(searchTimer.current);
          searchTimer.current = setTimeout(() => {
            setSearchParams(toParamsWithView(next), { replace: true });
          }, 400);
        }
        return next;
      });
      setPage(1);
      return;
    }
    setFilters(prev => {
      const next = { ...prev, ...patch };
      setSearchParams(toParamsWithView(next), { replace: true });
      return next;
    });
    setPage(1);
  }, [setSearchParams, toParamsWithView]);

  const onClear = useCallback(() => {
    const empty = buildFilters(new URLSearchParams());
    empty.limit = defaultPageSize;
    setFilters(empty);
    setSearchParams(toParamsWithView(empty), { replace: true });
    setPage(1);
  }, [defaultPageSize, setSearchParams, toParamsWithView]);

  const handleLimitChange = useCallback((nextLimit) => {
    setFilters((prev) => {
      const next = { ...prev, limit: nextLimit };
      setSearchParams(toParamsWithView(next), { replace: true });
      return next;
    });
    setPage(1);
    if (cardsOnlyMode) cardsColRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    else topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [setSearchParams, toParamsWithView]);

  const handlePageChange = (p) => {
    setPage(p);
    doFetch(filters, p, { append: false });
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const getBadge = (p) => {
    if (p.is_urgent)      return { label: 'Urgent',   icon: 'urgent',   color: '#ef4444' };
    if (p.is_highlighted) return { label: 'Featured', icon: 'featured', color: '#f59e0b' };
    if (p.is_project)     return { label: 'Project',  icon: 'project',  color: '#8b5cf6' };
    return {};
  };

  return (
    <div className={`pl-page${cardsOnlyMode ? ' pl-page--focus' : ''}`} ref={topRef}>

      {/* Top bar */}
      <div className={`pl-topbar${cardsOnlyMode ? ' pl-topbar--focus' : ''}`}>
        <div className="pl-result-info">
          <span className="pl-result-count">{isLoading ? '…' : total}</span>
          <span className="pl-result-label"> properties found</span>
          {!isLoading && total > filters.limit && (
            <span className="pl-result-page">
              &nbsp;— {cardsOnlyMode ? 1 : ((page - 1) * filters.limit + 1)}–{Math.min(cardsOnlyMode ? results.length : (page * filters.limit), total)}
            </span>
          )}
          {cardsOnlyMode && (
            <span className="pl-focus-hint">Press `Esc` to exit card view</span>
          )}
        </div>
        <div className="pl-topbar-actions">
          <button
            type="button"
            className={`pl-page-size-icon pl-page-size-icon-btn${cardsOnlyMode ? ' is-active' : ''}`}
            onClick={() => updateViewMode(!cardsOnlyMode)}
            aria-label={cardsOnlyMode ? 'Exit cards only mode' : 'Open cards only mode'}
            title={cardsOnlyMode ? 'Exit cards only mode' : 'Open cards only mode'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
          <label className="pl-page-size-card">
            <span className="pl-page-size-copy">
              <small>Show Per Page</small>
              <strong>Visible listings</strong>
            </span>
            <select className="pl-page-size-select" value={filters.limit} onChange={(e) => handleLimitChange(Number(e.target.value))}>
              {availablePageSizes.map((n) => (
                <option key={n} value={n}>{n} / page</option>
              ))}
            </select>
          </label>
          {cardsOnlyMode && (
            <button
              type="button"
              className="pl-exit-view-btn"
              onClick={() => updateViewMode(false)}
            >
              Exit View
            </button>
          )}
          {cardsOnlyMode && (
            <button
              type="button"
              className="pl-filter-toggle-btn"
              onClick={() => {
                if (isCompactViewport) setMobileFilter(true);
                else setFocusFiltersOpen((v) => !v);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                <line x1="4" y1="6" x2="20" y2="6"/>
                <line x1="8" y1="12" x2="16" y2="12"/>
                <line x1="11" y1="18" x2="13" y2="18"/>
              </svg>
              {isCompactViewport ? 'Show Filters' : (focusFiltersOpen ? 'Hide Filters' : 'Show Filters')}
            </button>
          )}
          {!cardsOnlyMode && <button className="pl-mob-filter-btn" onClick={() => setMobileFilter(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
              <line x1="4" y1="6" x2="20" y2="6"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
              <line x1="11" y1="18" x2="13" y2="18"/>
            </svg>
            Filters
          </button>}
        </div>
      </div>

      {/* Active chips */}
      {!cardsOnlyMode && <ActiveChips filters={filters} onChange={onChange} />}

      {/* Layout */}
      <div className={`pl-layout${cardsOnlyMode ? ' pl-layout--focus' : ''}`}>

        {/* LEFT — filter (sticky, self-scrolls) */}
        <FilterPanel
          filters={filters}
          onChange={onChange}
          onClear={onClear}
          mobileOpen={mobileFilter}
          onMobileClose={() => setMobileFilter(false)}
          desktopVisible={!isCompactViewport}
          desktopWrapperClassName={cardsOnlyMode ? 'pl-focus-filter-wrap' : ''}
          desktopCollapsed={cardsOnlyMode ? !focusFiltersOpen : false}
        />

        {/* RIGHT — cards */}
        <div className={`pl-cards-col${cardsOnlyMode ? ' pl-cards-col--focus' : ''}`} ref={cardsColRef}>
          {isLoading && results.length === 0 ? (
            <div className={`pl-grid${cardsOnlyMode ? ' pl-grid--focus' : ''}${cardsOnlyMode && focusFiltersOpen && !isCompactViewport ? ' pl-grid--with-filter' : ''}`}>
              {Array.from({ length: filters.limit || defaultPageSize }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : results.length === 0 ? (
            <div className="pl-empty">
              <div className="pl-empty-icon">
                <AppIcon name="home" size={48} />
              </div>
              <div className="pl-empty-title">No properties found</div>
              <div className="pl-empty-sub">Try adjusting or clearing your filters.</div>
              <button className="pl-empty-clear" onClick={onClear}>Clear All Filters</button>
            </div>
          ) : (
            <div className={`pl-grid${cardsOnlyMode ? ' pl-grid--focus' : ''}${cardsOnlyMode && focusFiltersOpen && !isCompactViewport ? ' pl-grid--with-filter' : ''}`}>
              {results.map(p => {
                const { label, color } = getBadge(p);
                return <PropertyCard key={p.id} property={p} badgeLabel={label} badgeColor={color} />;
              })}
            </div>
          )}
          {cardsOnlyMode ? (
            <>
              {isLoadingMore && (
                <div className={`pl-grid pl-grid--focus pl-grid-loading-more`}>
                  {Array.from({ length: Math.min(filters.limit || defaultPageSize, 4) }).map((_, i) => <SkeletonCard key={`more-${i}`} />)}
                </div>
              )}
              {!isLoading && page < totalPages && <div ref={loadMoreRef} className="pl-load-more-trigger" aria-hidden="true" />}
            </>
          ) : (
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={handlePageChange}
            />
          )}
        </div>
      </div>

      {/* Mobile FAB */}
      {!cardsOnlyMode && (
      <button className="pl-fab" onClick={() => setMobileFilter(true)}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
          <line x1="4" y1="6" x2="20" y2="6"/>
          <line x1="8" y1="12" x2="16" y2="12"/>
          <line x1="11" y1="18" x2="13" y2="18"/>
        </svg>
        Filters
      </button>
      )}
    </div>
  );
};

export default PropertyListing;